import { ComponentInteractionType, SceneComponent } from '@/_models/virtualStaging/Scene.component';
import { LoadingState } from '@/_models/virtualStaging/VSObject';
import { TextPlaneInputs, TextPlaneOutputs } from './textPlaneTypes';

export class TextPlaneComponent extends SceneComponent {
  public root: any = null;

  public textPlane: any = null;

  public isDestroyed = false;

  private textCanvas: HTMLCanvasElement;

  private textContext: CanvasRenderingContext2D;

  private raycaster: any;

  private mouse: any;

  private isTextHovered = false;

  inputs: TextPlaneInputs = {
    position: { x: 0, y: 0, z: 0 },
    rotation: { x: 0, y: 0, z: 0 },
    visible: true,
    text: 'Talk to Avatar (AI)',
    scale: 1,
    canvasWidth: 512,
    canvasHeight: 256,
    planeWidth: 2,
    planeHeight: 1,
    backgroundColor: 'rgba(40, 40, 60, 0.7)',
    textColor: '#ffffff',
    fontSize: 36,
    fontFamily: 'Arial',
    badgeText: 'AI',
    badgeBackgroundColor: 'rgba(255, 255, 255, 0.9)',
    badgeTextColor: '#000000',
    badgeFontSize: 12,
  };

  outputs: TextPlaneOutputs = {
    loadingState: LoadingState.Idle,
  };

  events = {
    [ComponentInteractionType.CLICK]: true,
    [ComponentInteractionType.HOVER]: true,
    [ComponentInteractionType.DRAG]: false,
  };

  declare context: any;

  onInit() {
    const THREE = this.context.three;
    this.root = new THREE.Object3D();
    this.root.position.set(this.inputs.position.x, this.inputs.position.y, this.inputs.position.z);
    this.root.rotation.set(this.inputs.rotation.x, this.inputs.rotation.y, this.inputs.rotation.z);
    this.root.visible = this.inputs.visible;
    this.root.scale.set(this.inputs.scale, this.inputs.scale, this.inputs.scale);

    // Set the required outputs that SceneComponent expects
    this.outputs.objectRoot = this.root;
    this.outputs.collider = this.root;

    this.createTextPlane();
    this.outputs.loadingState = LoadingState.Loaded;
  }

  private createTextPlane() {
    const THREE = this.context.three;

    // Create canvas for text
    this.textCanvas = document.createElement('canvas');
    this.textCanvas.width = this.inputs.canvasWidth;
    this.textCanvas.height = this.inputs.canvasHeight;
    this.textContext = this.textCanvas.getContext('2d');

    // Create texture from canvas
    const texture = new THREE.CanvasTexture(this.textCanvas);
    texture.needsUpdate = true;

    // Create material with the texture
    const material = new THREE.MeshBasicMaterial({
      map: texture,
      transparent: true,
      side: THREE.DoubleSide,
    });

    // Create plane geometry
    const geometry = new THREE.PlaneGeometry(this.inputs.planeWidth, this.inputs.planeHeight);

    // Create mesh with geometry and material
    this.textPlane = new THREE.Mesh(geometry, material);

    // Add to root
    this.root.add(this.textPlane);

    // Initialize raycaster for interaction
    this.raycaster = new THREE.Raycaster();
    this.mouse = new THREE.Vector2();

    // Initial text update
    this.updateTextContent(this.inputs.text);
  }

  updatePosition(model: any) {
    if (!model || !this.textPlane) return;

    const THREE = this.context.three;

    // Get model bounding box
    const boundingBox = new THREE.Box3().setFromObject(model);
    const center = new THREE.Vector3();
    boundingBox.getCenter(center);
    const size = new THREE.Vector3();
    boundingBox.getSize(size);

    // Position text directly in front of the model's center
    this.textPlane.position.set(
      center.x, // Same X as model center
      center.y, // Same Y as model center
      center.z + 0.3, // Fixed distance in front of model
    );
  }

  updateTextContent(text: string) {
    if (!this.textContext) return;

    // Clear the canvas
    this.textContext.clearRect(0, 0, this.textCanvas.width, this.textCanvas.height);

    // Set background with rounded corners - badge style with proper padding
    const bgColor = this.isTextHovered ? 'rgba(60, 60, 80, 0.8)' : this.inputs.backgroundColor;
    this.textContext.fillStyle = bgColor;

    // Use design specifications: 12px left/right padding, 7px top/bottom padding, 10px border radius
    const paddingX = 12;
    const paddingY = 7;
    const borderRadius = 10;

    this.roundRect(
      this.textContext,
      paddingX,
      paddingY,
      this.textCanvas.width - (paddingX * 2),
      this.textCanvas.height - (paddingY * 2),
      borderRadius,
      true,
    );

    // Set main text properties
    this.textContext.font = `${this.inputs.fontSize}px ${this.inputs.fontFamily}`;
    this.textContext.textAlign = 'center';
    this.textContext.textBaseline = 'middle';
    this.textContext.fillStyle = this.inputs.textColor;

    // Calculate text positioning - center the text and badge together
    const mainTextY = this.textCanvas.height / 2;

    // Measure text width to calculate total width with badge
    const textMetrics = this.textContext.measureText(text);
    const textWidth = textMetrics.width;
    const badgeWidth = this.inputs.badgeText ? 32 : 0; // Larger badge width
    const spaceBetween = this.inputs.badgeText ? 8 : 0; // Space between text and badge
    const totalWidth = textWidth + spaceBetween + badgeWidth;

    // Center the entire content (text + badge)
    const startX = (this.textCanvas.width - totalWidth) / 2;
    const mainTextX = startX + textWidth / 2;

    // Draw main text
    this.textContext.fillText(text, mainTextX, mainTextY);

    // Draw badge if badgeText is provided
    if (this.inputs.badgeText) {
      this.drawBadge();
    }

    // Update the texture
    this.textPlane.material.map.needsUpdate = true;
  }

  private drawBadge() {
    if (!this.textContext || !this.inputs.badgeText) return;

    // Measure main text width to position badge
    this.textContext.font = `${this.inputs.fontSize}px ${this.inputs.fontFamily}`;
    const textMetrics = this.textContext.measureText(this.inputs.text);
    const textWidth = textMetrics.width;

    // Badge dimensions and positioning - larger badge with more padding
    const badgeWidth = 32; // Increased from 24
    const badgeHeight = 24; // Increased from 16
    const badgeRadius = 12; // Increased radius for larger badge
    const spaceBetween = 8; // Space between text and badge

    // Calculate total width and center everything
    const totalWidth = textWidth + spaceBetween + badgeWidth;
    const startX = (this.textCanvas.width - totalWidth) / 2;
    const badgeX = startX + textWidth + spaceBetween;
    const badgeY = (this.textCanvas.height - badgeHeight) / 2;

    // Draw badge background
    this.textContext.fillStyle = this.inputs.badgeBackgroundColor || 'rgba(255, 255, 255, 0.9)';
    this.roundRect(
      this.textContext,
      badgeX,
      badgeY,
      badgeWidth,
      badgeHeight,
      badgeRadius,
      true,
    );

    // Draw badge text
    this.textContext.font = `bold ${this.inputs.badgeFontSize || 12}px ${this.inputs.fontFamily}`;
    this.textContext.textAlign = 'center';
    this.textContext.textBaseline = 'middle';
    this.textContext.fillStyle = this.inputs.badgeTextColor || '#000000';

    const badgeCenterX = badgeX + badgeWidth / 2;
    const badgeCenterY = badgeY + badgeHeight / 2;

    this.textContext.fillText(this.inputs.badgeText, badgeCenterX, badgeCenterY);
  }

  onEvent(eventType: string, eventData: any) {
    switch (eventType) {
      case ComponentInteractionType.CLICK:
        console.log('Text plane clicked!');
        // this.updateTextContent('You clicked me!\nTry saying something.');
        this.notify('textPlaneClicked', { text: this.inputs.text });
        break;
      case ComponentInteractionType.HOVER:
        // eventData.hover is true when mouse enters, false when mouse leaves
        this.isTextHovered = eventData.hover;
        document.body.style.cursor = this.isTextHovered ? 'pointer' : 'auto';
        // Update text content to show visual feedback on hover
        this.updateTextContent(this.inputs.text);
        break;
    }
  }

  onInputsUpdated(previousInputs: this['inputs']) {
    if (!this.root) return;

    this.root.visible = this.inputs.visible;
    this.root.position.set(this.inputs.position.x, this.inputs.position.y, this.inputs.position.z);
    this.root.rotation.set(this.inputs.rotation.x, this.inputs.rotation.y, this.inputs.rotation.z);
    this.root.scale.set(this.inputs.scale, this.inputs.scale, this.inputs.scale);

    // if (previousInputs.text !== this.inputs.text) {
    //   this.updateTextContent(this.inputs.text);
    // }
  }

  setVisible(visible: boolean) {
    this.inputs.visible = visible;
    if (this.root) {
      this.root.visible = visible;
    }
  }

  setText(text: string) {
    this.inputs.text = text;
    // this.updateTextContent(text);
  }

  onDestroy() {
    // Reset cursor when component is destroyed
    document.body.style.cursor = 'auto';

    if (this.textCanvas) {
      this.textCanvas = null;
    }
    if (this.textContext) {
      this.textContext = null;
    }
    this.isDestroyed = true;
  }

  /**
   * Helper function to draw rounded rectangles
   */
  private roundRect(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number,
    radius: number,
    fill: boolean,
  ) {
    ctx.beginPath();
    ctx.moveTo(x + radius, y);
    ctx.lineTo(x + width - radius, y);
    ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
    ctx.lineTo(x + width, y + height - radius);
    ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
    ctx.lineTo(x + radius, y + height);
    ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
    ctx.lineTo(x, y + radius);
    ctx.quadraticCurveTo(x, y, x + radius, y);
    ctx.closePath();
    if (fill) {
      ctx.fill();
    }
  }
}

export const textPlaneComponentType = 'mp.textPlaneComponent';
export const makeTextPlaneComponent = (): TextPlaneComponent => new TextPlaneComponent();
